import { Injectable, NgZone } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { fabric } from 'fabric';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SessionSharedDataService } from '../../../../services/session-shared-data.service';
import { SpaceRepository } from '../../../../state/space.repository';
import { LockSnackbarComponent } from '../../../../ui/lock-snackbar/lock-snackbar.component';
import { isBoardItem } from '../../../common/y-session';
import { BoardItem } from './board.item';

type LockableFabricObject = fabric.Object & { locked?: boolean };
@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class BoardItemLockService {
  private currentUserRoleSubscription?: Subscription;

  constructor(
    private sharedDataService: SessionSharedDataService,
    private spaceRepo: SpaceRepository,
    private ngZone: NgZone,
    private snackBar: MatSnackBar,
  ) {}

  private _lockStateChanged$: Subject<LockableFabricObject> = new Subject();
  public lockStateChanged$: Observable<LockableFabricObject> =
    this._lockStateChanged$.asObservable();

  public onLockStateChanged(fabricObject: fabric.Object) {
    this._lockStateChanged$.next(fabricObject as LockableFabricObject);
  }

  setLock(itemSelected: LockableFabricObject | BoardItem, shouldLock: boolean): void {
    if (!this.spaceRepo.isCurrentUserSpaceHost) {
      return;
    }

    // discard the active object then re-select it to avoid the case of locking a textbox in edit mode
    this.sharedDataService.fabricCanvas?.discardActiveObject();
    this.sharedDataService.fabricCanvas?.setActiveObject(itemSelected);

    if (isBoardItem(itemSelected)) {
      itemSelected.toggleLockState();
      return;
    }

    // update locked attribute state
    itemSelected.set('locked', shouldLock);

    // sync change with other users
    this.sharedDataService.fabricCanvas?.fire('object:modified', { target: itemSelected });
    // calling onSelect to refresh the selection state
    itemSelected.onSelect({});
    this.sharedDataService.fabricCanvas?.requestRenderAll();

    this.onLockStateChanged(itemSelected);
  }

  /**
   * Toggle lock state of an object (lock if unlocked, unlock if locked)
   */
  toggleLock(itemSelected: LockableFabricObject | BoardItem): void {
    if (!this.spaceRepo.isCurrentUserSpaceHost) {
      return;
    }

    // Determine if we should lock or unlock based on current state
    let shouldLock: boolean;
    if (isBoardItem(itemSelected)) {
      // For board items, check the configurations.locked property
      shouldLock = !itemSelected.configurations.locked;
    } else {
      // For regular fabric objects, check the locked property
      shouldLock = !((itemSelected as any).locked === true);
    }

    this.setLock(itemSelected, shouldLock);
  }

  showLockSnackBar(boardItem: BoardItem) {
    this.ngZone.runOutsideAngular(() => {
      if (this.snackBar._openedSnackBarRef) {
        this.snackBar.dismiss();
        this.snackBar._openedSnackBarRef.afterDismissed();
      }
      this.snackBar
        .openFromComponent(LockSnackbarComponent, {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: 'lock-snack-bar',
          data: boardItem,
        })
        .afterDismissed();
    });
  }

  public initService() {
    // To refresh the context menu of the currently active object in case the lock state was affected by changes in the user role.
    this.currentUserRoleSubscription?.unsubscribe();
    this.currentUserRoleSubscription = this.spaceRepo.currentUserRole$
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        const activeObjects = this.sharedDataService.fabricCanvas?.getActiveObjects();
        if (activeObjects?.length === 1) {
          this.onLockStateChanged(activeObjects[0]);
        }
      });
  }

  public destroyService() {
    this.currentUserRoleSubscription?.unsubscribe();
  }
}
